import { useEffect, useState } from "react";
import AppHeader from "@/components/tpr/AppHeader/AppHeader";
import AppSidebar from "@/components/tpr/AppSidebar/AppSidebar";
import { ErrorBoundary, LayoutErrorFallback } from "@/components/tpr/ErrorBoundary";
import MaintenanceOverlay from "@/components/tpr/MaintenanceOverlay/MaintenanceOverlay";
import NotificationsBar from "@/components/tpr/NotificationsBar/NotificationsBar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/sonner";
import { Spinner } from "@/components/ui/spinner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BreadcrumbProvider } from "@/contexts/breadcrumbContext";
import { FontProvider } from "@/contexts/fontContext";
import { ThemeProvider } from "@/contexts/themeContext";
import { tprStorage } from "@/core/storage";
import { LiveMemberRefresh } from "@/components/tpr/LiveMemberRefresh";
import { MaintenanceProvider } from "@/providers/MaintenanceProvider";
import { MemberProvider } from "@/providers/MemberProvider";
import { BookingService } from "@/services/BookingService";
import { nextGatePath } from "@/utils/loginGates";
import { Frown } from "lucide-react";
import { Navigate } from "react-router";
import SidebarAwareLayout from "./SidebarAwareLayout";
import type { Member } from "@/types/Member";
import type { PropsWithChildren } from "react";
import "../styles/viewTransitions.css";
import DeploymentNotification from "@/components/tpr/DeploymentNotification/DeploymentNotification";

// Sentinel so loadMember's catch can tell "session is gone" (handled via a login redirect)
// apart from a genuine data-load failure (handled via the error screen).
class SessionExpiredError extends Error {}

const TPR = (props: PropsWithChildren) => {
  const [member, setMember] = useState<Member | null>(null);
  const [todaysBookings, setTodaysBookings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const loadMember: () => Promise<{ member: Member; bookings: number }> = async () => {
    setLoading(true);
    setIsError(false);
    try {
      const storedMember = await Promise.resolve(tprStorage.get<Member>("tpruser"));

      // A missing cached member means the session is gone — fetchWithAuth wipes `tpruser`
      // on a 401, as do logout and cleared cookies. Redirect to login rather than
      // dereferencing null.id, which threw a TypeError that was the dominant source of the
      // "Failed to load member data" noise (Sentry EMBER-2).
      if (!storedMember) {
        setSessionExpired(true);
        throw new SessionExpiredError("No cached member; session expired");
      }

      const bookingService = new BookingService();
      const todaysBookings = await bookingService.getBookingsForMemberOnDate(storedMember.id, new Date());
      setMember(storedMember);
      setTodaysBookings(todaysBookings.length);

      return { member: storedMember, bookings: todaysBookings.length };
    } catch (error) {
      // The session-gone case is already handled via the sessionExpired redirect; re-throw
      // without flipping the error screen or masking the cause.
      if (error instanceof SessionExpiredError) {
        throw error;
      }
      console.error("Error fetching member data:", error);
      setIsError(true);
      // Preserve the original error as the cause so Sentry records the *real* failure
      // (network, 401, serialization, …) instead of this generic mask.
      throw new Error("Failed to load member data", { cause: error });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fire-and-forget on mount: every outcome is already reflected in component state
    // (`sessionExpired` → login redirect, `isError` → error screen), so swallow the
    // rejection here to prevent an unhandled promise rejection from flooding Sentry
    // (EMBER-2). The throw is retained for the MemberProvider `onRefresh` caller below,
    // which consumes the resolved value and handles rejection itself.
    loadMember().catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="h-screen w-screen flex justify-center items-center flex-col gap-6">
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Just a moment...</span>
      </div>
    );
  }

  if (sessionExpired) {
    return <Navigate to="/login?sessionExpired=true" replace />;
  }

  if (isError || !member) {
    return (
      <div className="h-screen w-screen flex justify-center items-center flex-col gap-6">
        <Frown />
        <span className="text-lg">
          Sorry. But an error has occurred. Please try again and if the problem persists clear your cookies and try
          again. Finally, if that doesn't solve the issue, please contact support.
        </span>
      </div>
    );
  }

  // Route guard: an authenticated member who hasn't cleared the terms / annual-declaration gates is
  // sent back to the gate, no matter how they reached a protected route (e.g. the browser back
  // button after login). This is the single chokepoint — every protected route is TPR-wrapped.
  const gate = nextGatePath(member);
  if (gate) {
    return <Navigate to={gate} replace />;
  }

  return (
    <div id="tpr">
      <TooltipProvider>
        <MemberProvider initialMember={member} initialBookings={todaysBookings} onRefresh={loadMember}>
          {/* Keeps the member in sync with the server (mount / focus / socket) so
              role/permission changes land live without a logout/login. Owns the shared
              booking socket listener too. */}
          <LiveMemberRefresh />
          <MaintenanceProvider>
            <MaintenanceOverlay />
            <ThemeProvider>
              <FontProvider>
                <SidebarProvider
                  style={
                    {
                      "--sidebar-width": "16rem", // 256px
                      "--header-height": "calc(var(--spacing) * 12)",
                    } as React.CSSProperties
                  }
                >
                  <AppSidebar variant="inset" />
                  <SidebarInset>
                    <BreadcrumbProvider>
                      <SidebarAwareLayout>
                        <DeploymentNotification />
                        <NotificationsBar hidden />
                        <AppHeader />
                        <div className="flex flex-1 flex-col">
                          <div className="@container/main flex flex-1 flex-col gap-2" id="tpr-main-content">
                            <ErrorBoundary level="layout" fallback={LayoutErrorFallback}>
                              {props.children}
                            </ErrorBoundary>
                          </div>
                        </div>
                      </SidebarAwareLayout>
                    </BreadcrumbProvider>
                  </SidebarInset>
                </SidebarProvider>
              </FontProvider>
            </ThemeProvider>
          </MaintenanceProvider>
        </MemberProvider>
      </TooltipProvider>
      <Toaster />
    </div>
  );
};

export default TPR;
